import { useCallback, useEffect, useState } from "react";
import { Loader2, ScanSearch, RefreshCw, FileDiff, Mail, ShoppingCart, Repeat, AlertTriangle, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { opsApi } from "../../lib/api";
import SideBySideViewer from "./SideBySideViewer";
import FrameworkEmailDialog from "../../components/FrameworkEmailDialog";
import { openInSubBuilder } from "./navigation";

const ICON = { co: FileDiff, rfi: Mail, po: ShoppingCart, resubmit: Repeat };
const TYPE_ORDER = ["rejection", "question", "quantity", "scope_add", "scope_delete", "product", "dimension", "finish", "note"];

/** SUB-2 — Review panel on a returned revision: scan state, change list grouped by type with region
 *  thumbnails (→ side-by-side viewer), and the directives as neutral outlined buttons; the
 *  recommended one carries a chip. Each opens a PREFILLED draft in its own module. */
export default function ReviewPanel({ project, rev, canEdit, onChanged }) {
  const [review, setReview] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [rfi, setRfi] = useState(null);
  const [busy, setBusy] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const go = (patch) => {
    const params = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([k, v]) => (v == null ? params.delete(k) : params.set(k, v)));
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };
  const hasReturn = Boolean(rev.returned?.document_id || rev.returned_document_id);

  const load = useCallback(() => opsApi.submittalReview(project.id, rev.id).then(setReview).catch(() => setReview({ state: "none", findings: [] })), [project.id, rev.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!review || !["queued", "running"].includes(review.state)) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [review, load]);

  if (!hasReturn) return null;
  if (!review) return <div className="flex items-center gap-2 py-3 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading review…</div>;

  const run = async (key) => {
    setBusy(key);
    try {
      const r = await opsApi.submittalDirective(project.id, rev.id, key);
      if (key === "co") {
        toast.success(`${r.number} drafted with ${r.lines} line(s) — review pricing before it goes out`);
        go({ otab: "change_orders", opsco: r.change_order_id, pkg: null });
      } else if (key === "rfi") {
        setRfi(r);
        return;                                   // refresh when the draft closes, not now
      } else if (key === "po") {
        toast.success(`PO ${r.prepared.join(", ")} prepared — ${r.released_by}`);
        go({ otab: "purchasing", pkg: null });
      } else if (key === "resubmit") {
        toast.success(`${r.revision.number} Rev ${r.revision.revision} opened with ${r.checklist.length} item(s) to address`);
        if (r.open?.subbuilder_submittal_id) {
          openInSubBuilder({ projectId: r.open.subbuilder_project_id, submittalId: r.open.subbuilder_submittal_id, number: r.revision.number,
                             opsProjectId: project.id, opsProjectName: project.identity?.name || project.display || "", jobNumber: project.identity?.job_number || project.job_number });
        }
      }
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That directive could not be prepared"); }
    finally { setBusy(""); }
  };

  const groups = TYPE_ORDER.map((t) => [t, (review.findings || []).filter((f) => f.type === t)]).filter(([, fs]) => fs.length);
  const state = review.state;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs" data-testid={`ops-review-panel-${rev.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-1 font-semibold text-slate-700"><ScanSearch className="h-3.5 w-3.5" /> Review</p>
        <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${state === "done" ? "bg-emerald-100 text-emerald-700" : state === "incomplete" ? "bg-amber-100 text-amber-800" : state === "failed" ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"}`}
              data-testid="ops-review-state">{state === "none" ? "not scanned" : state}</span>
        {["queued", "running"].includes(state) && <Loader2 className="h-3 w-3 animate-spin text-sky-600" />}
        {review.status_read?.phrase && <span className="text-slate-500">stamp read “{review.status_read.phrase}”{review.status_read.exact ? " (exact)" : " (proposed)"}</span>}
        {canEdit && (
          <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" disabled={busy === "rescan" || ["queued", "running"].includes(state)}
                  onClick={async () => { setBusy("rescan"); try { await opsApi.submittalRescan(project.id, rev.id); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not queue the scan"); } finally { setBusy(""); } }}
                  data-testid="ops-review-rescan">
            <RefreshCw className="mr-1 h-3 w-3" /> {state === "none" ? "Scan now" : "Rescan"}
          </Button>
        )}
      </div>
      {(review.incomplete_reasons || []).length > 0 && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900" data-testid="ops-review-incomplete">
          <p className="flex items-center gap-1 font-medium"><AlertTriangle className="h-3 w-3" /> Review incomplete — act manually where needed</p>
          <ul className="mt-0.5 list-disc pl-4">{review.incomplete_reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
      {review.error && <p className="mt-2 text-rose-700">{review.error}</p>}

      {state !== "none" && (
        <div className="mt-3">
          <p className="font-semibold text-slate-700">Changes found · {(review.findings || []).length}</p>
          {groups.length === 0 && <p className="mt-1 text-slate-500">{["queued", "running"].includes(state) ? "Scanning…" : "No markups or changed regions were found."}</p>}
          <div className="mt-1 space-y-2">
            {groups.map(([t, fs]) => (
              <div key={t} data-testid={`ops-review-group-${t}`}>
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{fs[0].label} · {fs.length}{fs[0].cost_affecting ? " · cost-affecting" : ""}</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {fs.map((f) => (
                    <button key={f.id} onClick={() => setViewer({ page: f.page_returned, focusId: f.id })} data-testid={`ops-review-finding-${f.id}`}
                            className="flex w-64 items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-1.5 text-left hover:border-sky-300 hover:bg-sky-50">
                      {f.thumb_b64 && <img src={`data:image/png;base64,${f.thumb_b64}`} alt="" className="h-14 w-20 flex-none rounded border border-slate-200 bg-white object-cover" />}
                      <span className="min-w-0">
                        <span className="block truncate text-slate-800">{f.text || <em className="text-slate-400">no readable text</em>}</span>
                        <span className="block text-[10px] text-slate-500">p.{f.page_returned + 1} · {f.source === "annotation" ? "markup" : "visual diff"} · {f.reasoning}</span>
                        {(f.frs_rows || []).length > 0 && <span className="block text-[10px] text-violet-700">FRS: {f.frs_rows.map((r) => r.model || r.room).join(", ")}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {canEdit && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="ops-review-directives">
              {(review.directives || []).map((d) => { const Icon = ICON[d.key]; return (
                <Button key={d.key} size="sm" variant="outline" className="h-8 text-xs" disabled={Boolean(busy)} onClick={() => run(d.key)}
                        data-testid={`ops-review-directive-${d.key}`}>
                  {busy === d.key ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Icon className="mr-1.5 h-3.5 w-3.5" />} {d.label}
                  {d.recommended && <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Recommended</span>}
                </Button>
              ); })}
            </div>
          )}
        </div>
      )}

      {(rev.resubmit_checklist || []).length > 0 && (
        <div className="mt-3 rounded-md border border-violet-200 bg-violet-50 p-2" data-testid="ops-review-checklist">
          <p className="font-medium text-violet-900">To address in this revision</p>
          <ul className="mt-1 space-y-0.5">
            {rev.resubmit_checklist.map((c) => (
              <li key={c.finding_id} className="flex items-start gap-1.5 text-violet-900">
                <button disabled={!canEdit} onClick={() => opsApi.submittalChecklist(project.id, rev.id, { finding_id: c.finding_id, done: !c.done }).then(() => onChanged?.())}
                        data-testid={`ops-review-check-${c.finding_id}`}>
                  {c.done ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                </button>
                <span className={c.done ? "line-through opacity-60" : ""}>{c.label}: {c.text || "see markup"} <span className="text-violet-500">· p.{c.page}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {viewer && <SideBySideViewer project={project} rev={rev} review={review} page={viewer.page} focusId={viewer.focusId} onClose={() => setViewer(null)} />}
      {rfi && <FrameworkEmailDialog projectId={project.id} draft={rfi} onClose={() => setRfi(null)} onSent={() => { setRfi(null); onChanged?.(); }} />}
    </div>
  );
}
